import {Ast, ExpressionCompilerService} from '@ngmedax/expression-compiler';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {TemplateVariable} from '../../froala/template-variables';
import {KEYS} from '../../../translation-keys';

/** Describes a selectable dependency source and the input appropriate for its runtime value. */
export interface DependencySource extends TemplateVariable {
  valueType: 'text' | 'number' | 'boolean' | 'date';
  dateInput?: 'date' | 'month' | 'number' | 'datetime-local';
  choices?: {value: string; label: string}[];
}

/** Names comparisons without changing the dependency engine's existing operators. */
export const DEPENDENCY_OPERATORS: {[operator: string]: string} = {
  '==': KEYS.EDITOR.CONDITION_EQ, '!=': KEYS.EDITOR.CONDITION_NE,
  '>': KEYS.EDITOR.CONDITION_GT, '>=': KEYS.EDITOR.CONDITION_GTE,
  '<': KEYS.EDITOR.CONDITION_LT, '<=': KEYS.EDITOR.CONDITION_LTE, '~': 'entspricht dem Muster'
};

/** Creates a detached draft with no implicit condition on the questionnaire. */
export function emptyDependency(): Ast.Group {
  return {type: 'group', operator: '&&', elements: []};
}

/** Creates an incomplete rule that must be completed before applying the draft. */
export function newDependencyRule(): Ast.Expression {
  return {type: 'expression', variable: '', operator: '==', value: ''};
}

/** Inverse comparisons, regex matches have none in the expression grammar */
const NEGATED_OPERATORS: {[operator: string]: Ast.OPERATOR} = {
  '==': '!=', '!=': '==', '>': '<=', '<=': '>', '<': '>=', '>=': '<'
};

/** The grammar has no NOT, a group is negated by De Morgan: swap AND and OR and invert every comparison */
export function canNegateDependency(group: Ast.Group): boolean {
  return !!group.elements.length && group.elements.every(element => element.type === 'group' ?
    canNegateDependency(element as Ast.Group) : !!NEGATED_OPERATORS[element.operator]);
}

export function negateDependency(group: Ast.Group): void {
  group.operator = group.operator === '||' ? '&&' : '||';
  group.elements.forEach(element => element.type === 'group' ? negateDependency(element as Ast.Group) :
    element.operator = NEGATED_OPERATORS[element.operator]);
}

/** Compares token streams to catch syntax that the legacy parser silently normalizes or drops. */
function tokenSignature(source: string, compiler: ExpressionCompilerService): string {
  return JSON.stringify(compiler.tokenize(source));
}

/** Reads the effective expression, retaining precedence over legacy answer dependencies. */
export function readDependency(conditions: Questionnaire.Container['conditions'], compiler: ExpressionCompilerService): Ast.Group {
  if (conditions?.expression) {
    const group = compiler.parse(conditions.expression);
    if (tokenSignature(conditions.expression, compiler) !== tokenSignature(compiler.compile(group), compiler)) {
      throw new Error('Dependency cannot be represented without changing its expression.');
    }
    if (!group.elements.length) { throw new Error('An empty expression is not a valid visibility condition.'); }
    /** Supplies a visual group mode where a single rule has no explicit logical operator. */
    const normalize = (node: Ast.Group): void => {
      if (!node.operator) { node.operator = '&&'; }
      node.elements.filter(element => element.type === 'group').forEach(element => normalize(element as Ast.Group));
    };
    normalize(group);
    return group;
  }
  const show = conditions?.show;
  const hashes = typeof show === 'string' ? [show] : show || [];
  if (!hashes.length) { return emptyDependency(); }
  return {type: 'group', operator: '||', elements: hashes.map(hash => ({
    type: 'expression', variable: `container.x${hash}`, operator: '==', value: true
  }))};
}

/** Resolves legacy patient aliases while preserving the original variable in saved expressions. */
export function dependencySource(path: string, sources: DependencySource[]): DependencySource {
  return sources.find(source => source.path === path?.replace(/^patient\./, 'client.'));
}

/** Preserves existing literal types even when a source's configured format has changed. */
export function dependencyValueType(rule: Ast.Expression, sources: DependencySource[]): string {
  const source = dependencySource(rule.variable, sources);
  if (source?.valueType === 'date' && (typeof rule.value === 'string' || (rule.value as any) instanceof Date)) { return 'date'; }
  if (typeof rule.value === 'boolean') { return 'boolean'; }
  if (typeof rule.value === 'number' || rule.value === null) { return 'number'; }
  return 'text';
}

/** Validates one rule against available references and the actual legacy expression grammar. */
export function dependencyRuleIssue(rule: Ast.Expression, sources: DependencySource[], compiler: ExpressionCompilerService): string {
  if (!rule.variable) { return 'Bitte wählen Sie eine Frage oder Variable.'; }
  if (!dependencySource(rule.variable, sources)) { return 'Diese Antwort oder Variable existiert nicht mehr. Bitte ersetzen Sie sie.'; }
  if (!/^[$a-zA-Z_][$a-zA-Z0-9_]*(\.[$a-zA-Z_][$a-zA-Z0-9_]*)*$/.test(rule.variable)) {
    return 'Dieser Variablenname wird in Bedingungen nicht unterstützt.';
  }
  if (!DEPENDENCY_OPERATORS[rule.operator]) { return 'Dieser Vergleich wird nicht unterstützt.'; }
  if (rule.value === null || rule.value === undefined || (typeof rule.value === 'number' && !Number.isFinite(rule.value))) {
    return 'Bitte geben Sie einen gültigen Vergleichswert ein.';
  }
  const source = dependencySource(rule.variable, sources);
  if (source.valueType === 'date' && rule.operator !== '~') {
    const value = rule.value as any;
    const isDate = value instanceof Date;
    const input = source.dateInput || 'date';
    const pattern = input === 'number' ? /^\d{4}$/ : input === 'month' ? /^\d{4}-(0[1-9]|1[0-2])$/ :
      /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    if (isDate ? !Number.isFinite(value.getTime()) : !pattern.test(String(value))) { return 'Bitte geben Sie ein gültiges Datum ein.'; }
    if (!isDate && input === 'date' && new Date(value).toISOString().slice(0, 10) !== value) {
      return 'Bitte geben Sie ein gültiges Datum ein.';
    }
  }
  if (rule.operator === '~') {
    if (typeof rule.value !== 'string' || !rule.value || /[\/\r\n]/.test(rule.value)) { return 'Bitte geben Sie ein gültiges Muster ohne Schrägstriche ein.'; }
    try { new RegExp(rule.value); } catch (error) { return 'Bitte geben Sie ein gültiges Muster ohne Schrägstriche ein.'; }
  } else if (typeof rule.value === 'string' && /[\\\r\n]/.test(rule.value)) {
    return 'Die vorhandene Auswertung unterstützt diesen Vergleichstext nicht.';
  }
  try {
    const group: Ast.Group = {type: 'group', operator: '&&', elements: [rule]};
    const expression = compiler.compile(group);
    const parsed = compiler.parse(expression);
    const result = parsed.elements[0] as Ast.Expression;
    if (parsed.elements.length !== 1 || result.type !== 'expression' || result.variable !== rule.variable ||
        result.operator !== rule.operator || JSON.stringify(result.value) !== JSON.stringify(rule.value)) {
      return 'Die vorhandene Auswertung unterstützt diesen Vergleichstext nicht.';
    }
  } catch (error) { return 'Die vorhandene Auswertung unterstützt diesen Vergleichstext nicht.'; }
  return '';
}

/** Rejects incomplete nested groups while allowing an empty root to mean always visible. */
export function dependencyIssue(group: Ast.Group, sources: DependencySource[], compiler: ExpressionCompilerService): string {
  if (!group) { return 'Dieser Ausdruck kann nicht verlustfrei im visuellen Editor bearbeitet werden.'; }
  for (const element of group.elements) {
    const issue = element.type === 'expression' ? dependencyRuleIssue(element as Ast.Expression, sources, compiler) :
      !(element as Ast.Group).elements.length ? 'Bitte ergänzen oder entfernen Sie die leere Gruppe.' :
      dependencyIssue(element as Ast.Group, sources, compiler);
    if (issue) { return issue; }
  }
  return '';
}

/** Describes safe, separately styled fragments of a readable dependency summary. */
export interface DependencySummaryPart {
  kind: 'source' | 'text' | 'join' | 'bracket';
  text: string;
  title?: string;
  source?: DependencySource;
}

/** Builds plain text fragments so variable chips never need HTML interpolation. */
export function dependencySummaryParts(group: Ast.Group, sources: DependencySource[],
  translate: (text: string) => string): DependencySummaryPart[] {
  if (!group) { return [{kind: 'text', text: translate('Vorhandener Ausdruck')}]; }
  if (!group.elements.length) { return [{kind: 'text', text: translate('Diese Frage wird immer angezeigt.')}]; }
  const parts: DependencySummaryPart[] = [];
  group.elements.forEach((element, index) => {
    if (index) { parts.push({kind: 'join', text: ` ${translate(group.operator === '||' ? 'ODER' : 'UND')} `}); }
    if (element.type === 'group') {
      parts.push({kind: 'bracket', text: '('}, ...dependencySummaryParts(element as Ast.Group, sources, translate),
        {kind: 'bracket', text: ')'});
      return;
    }
    const rule = element as Ast.Expression;
    const source = dependencySource(rule.variable, sources);
    const label = source?.label || rule.variable || translate('Frage oder Variable wählen');
    parts.push({kind: 'source', text: label, title: rule.variable ? `${label}\n${rule.variable}` : label, source});
    if (source?.group === 'answers' && typeof rule.value === 'boolean' && ['==', '!='].indexOf(rule.operator) !== -1) {
      const predicate = rule.operator + ':' + rule.value;
      const labels = {'==:true': 'ist ausgewählt', '!=:true': 'ist nicht ausgewählt',
        '==:false': 'ist ausdrücklich abgewählt', '!=:false': 'ist nicht ausdrücklich abgewählt'};
      parts.push({kind: 'text', text: ` ${translate(labels[predicate])}`});
      return;
    }
    const choice = source?.choices?.find(item => item.value === rule.value);
    const value = choice?.label || (typeof rule.value === 'boolean' ? translate(rule.value ? 'Ja' : 'Nein') :
      (rule.value as any) instanceof Date ? (rule.value as any as Date).toISOString() : String(rule.value ?? '…'));
    parts.push({kind: 'text', text: ` ${translate(DEPENDENCY_OPERATORS[rule.operator] || rule.operator)} ${value === '' ? '“”' : value}`});
  });
  return parts;
}

/** Keeps the complete accessible text independent of visual truncation. */
export function dependencySummary(group: Ast.Group, sources: DependencySource[], translate: (text: string) => string): string {
  return dependencySummaryParts(group, sources, translate).map(part => part.text).join('');
}

/** Replaces only visibility dependencies, preserving marking and any other condition metadata. */
export function applyDependency(conditions: Questionnaire.Container['conditions'], group: Ast.Group,
  compiler: ExpressionCompilerService): Questionnaire.Container['conditions'] {
  const next = {...conditions};
  delete next.expression;
  delete next.show;
  if (!group.elements.length) { return next; }
  const simple = (group.operator === '||' || group.elements.length === 1) && group.elements.every(element => {
    const rule = element as Ast.Expression;
    return rule.type === 'expression' && /^container\.x[a-zA-Z0-9_]+$/.test(rule.variable) &&
      rule.operator === '==' && rule.value === true;
  });
  if (simple) {
    next.show = group.elements.map(element => (element as Ast.Expression).variable.replace(/^container\.x/, ''));
  } else {
    next.expression = compiler.compile(group);
  }
  return next;
}
